import ChannelsService from '@/services/channels.service';

import { resolveChannelOwnerProfileType, resolveImageId } from './channelMessageHelpers';

export const CHANNELS_DISCOVER_PATH = '/channels/discover';

export function normalizeChannelId(raw) {
  if (!raw) return null;
  if (typeof raw === 'string' || typeof raw === 'number') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    if (raw.$oid) return String(raw.$oid);
    return normalizeChannelId(raw._id || raw.id || raw.channelId || raw.channel_id || null);
  }
  return null;
}

export function resolveChannelOwnerUsername(channel) {
  return (
    channel?.owner_username ||
    channel?.ownerUsername ||
    channel?.channel?.owner_username ||
    channel?.owner?.user_username ||
    null
  );
}

export function resolveChannelTitle(channel) {
  return (
    channel?.title ||
    channel?.channel_name ||
    channel?.name ||
    channel?.owner?.channel_name ||
    resolveChannelOwnerUsername(channel) ||
    'Untitled channel'
  );
}

export function resolveChannelOwnerImage(channel) {
  return (
    resolveImageId(channel?.owner_image) ||
    resolveImageId(channel?.ownerImage) ||
    resolveImageId(channel?.owner?.user_image) ||
    resolveImageId(channel?.channel?.owner?.user_image) ||
    null
  );
}

export function resolveChannelLastMessage(channel) {
  return (
    channel?.lastMessage ||
    channel?.last_message ||
    channel?.latestMessage ||
    channel?.summary?.lastMessage ||
    null
  );
}

export function resolveChannelLastActivity(channel) {
  const lastMessage = resolveChannelLastMessage(channel);
  return (
    channel?.lastActivity ||
    channel?.lastActivityAt ||
    channel?.updatedAt ||
    channel?.updated_at ||
    lastMessage?.createdAt ||
    lastMessage?.created_at ||
    lastMessage?.timestamp ||
    channel?.createdAt ||
    channel?.created_at ||
    null
  );
}

export function getChannelActivityTime(channel) {
  const activity = resolveChannelLastActivity(channel);
  if (!activity) return 0;
  const parsed = new Date(activity).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function buildChannelJoinRoute(channel, fallbackPath = CHANNELS_DISCOVER_PATH) {
  const channelId = normalizeChannelId(channel?._id || channel?.id || channel?.channelId || channel?.channel_id);
  const ownerUsername = resolveChannelOwnerUsername(channel);

  if (channelId && ownerUsername) {
    return `/channel/${ownerUsername}/${channelId}/join`;
  }

  return fallbackPath;
}

export async function hydrateDiscoverChannel(channel) {
  const channelId = normalizeChannelId(channel?._id || channel?.id || channel?.channelId || channel?.channel_id);
  if (!channelId) return null;

  let nextChannel = { ...channel };
  const existingOwnerUsername = resolveChannelOwnerUsername(nextChannel);
  const existingTitle = resolveChannelTitle(nextChannel);
  const existingOwnerImage = resolveChannelOwnerImage(nextChannel);
  const existingOwnerType =
    nextChannel?.ownerType ||
    nextChannel?.owner_type ||
    nextChannel?.owner?.user_type ||
    nextChannel?.owner?.__t ||
    nextChannel?.channel_kind ||
    nextChannel?.channelKind ||
    null;

  if (!existingOwnerUsername || !existingTitle || !existingOwnerImage || !existingOwnerType) {
    try {
      const { response, data } = await ChannelsService.getOverview(channelId);
      if (response?.ok && data) {
        nextChannel = {
          ...nextChannel,
          ...data,
          owner: data?.owner || nextChannel?.owner || null,
          owner_username:
            data?.owner_username ||
            data?.owner?.user_username ||
            nextChannel?.owner_username ||
            nextChannel?.ownerUsername ||
            null,
          title:
            data?.title ||
            data?.channel_name ||
            nextChannel?.title ||
            nextChannel?.channel_name ||
            null,
        };
      }
    } catch (_) {
      // Keep the existing payload if hydration fails.
    }
  }

  const ownerUsername = resolveChannelOwnerUsername(nextChannel);
  if (!ownerUsername) return null;

  const ownerType = resolveChannelOwnerProfileType(
    nextChannel?.ownerType ||
      nextChannel?.owner_type ||
      nextChannel?.owner?.user_type ||
      nextChannel?.owner?.__t ||
      nextChannel?.channel_kind ||
      nextChannel?.channelKind ||
      nextChannel?.channel?.owner?.user_type ||
      nextChannel?.channel?.owner?.__t ||
      'collector',
    'collector'
  );

  return {
    ...nextChannel,
    _id: channelId,
    owner_username: ownerUsername,
    ownerUsername,
    title: resolveChannelTitle(nextChannel),
    owner_image: resolveChannelOwnerImage(nextChannel),
    ownerImage: resolveChannelOwnerImage(nextChannel),
    owner_user_type: ownerType,
    ownerType,
    lastActivity: resolveChannelLastActivity(nextChannel),
    latestMessage: resolveChannelLastMessage(nextChannel),
  };
}
